export enum UserRole {
  ADMIN = 'admin',
  /** Operational shop employee (formerly `staff`). Full shop-floor access. */
  ATTENDANT = 'attendant',
  /** Shop-floor attendant plus Customers-tab access matching Admin. */
  ATTENDANT_PLUS = 'attendant_plus',
  /**
   * Legacy enum value; Phase 2+ `staff` means attendance + profile only.
   * Prefer ATTENDANT for operational shop-floor access.
   */
  STAFF = 'staff',
  SUPER_ADMIN = 'super_admin',
  TUNNEL = 'tunnel',
}

export interface RolePermissions {
  canViewDashboard: boolean;
  canManageUsers: boolean;
  canManageAppointments: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
  canViewAttendance: boolean;
  canManageAttendance: boolean;
  canViewAnalytics: boolean;
  canManagePackages: boolean;
  canManageCustomers: boolean;
  canViewCRM: boolean;
  canManageFleet: boolean;
  canManageEmployees: boolean;
  canViewOrders: boolean;
  canManageOrders: boolean;
  canCancelOrders: boolean;
  canAddOrders: boolean;
  canViewPayments: boolean;
}

export interface RoleConfig {
  role: UserRole;
  displayName: string;
  defaultRoute: string;
  allowedRoutes: string[];
  permissions: RolePermissions;
}

/**
 * Returns a RolePermissions object with all permissions set to false,
 * then applies any provided overrides. Reduces repetition in ROLE_CONFIGS.
 */
function createDefaultPermissions(overrides: Partial<RolePermissions> = {}): RolePermissions {
  return {
    canViewDashboard: false,
    canManageUsers: false,
    canManageAppointments: false,
    canViewReports: false,
    canManageSettings: false,
    canViewAttendance: false,
    canManageAttendance: false,
    canViewAnalytics: false,
    canManagePackages: false,
    canManageCustomers: false,
    canViewCRM: false,
    canManageFleet: false,
    canManageEmployees: false,
    canViewOrders: false,
    canManageOrders: false,
    canCancelOrders: false,
    canAddOrders: false,
    canViewPayments: false,
    ...overrides,
  };
}

/** Operational shop-floor routes/permissions (attendant; Phase 1 also used for legacy staff). */
const ATTENDANT_ROLE_CONFIG: RoleConfig = {
  role: UserRole.ATTENDANT,
  displayName: 'Attendant',
  defaultRoute: '/attendance',
  allowedRoutes: [
    '/attendance',
    '/appointments',
    '/orders',
    '/profile',
    '/rewards',
    '/sales',
    '/notifications',
    '/camera',
    '/cash-reconciliation',
  ],
  permissions: createDefaultPermissions({
    canManageAppointments: true,
    canViewAttendance: true,
    canViewOrders: true,
  }),
};

export const ROLE_CONFIGS: Record<UserRole, RoleConfig> = {
  [UserRole.ADMIN]: {
    role: UserRole.ADMIN,
    displayName: 'Administrator',
    defaultRoute: '/dashboard',
    allowedRoutes: [
      '/dashboard',
      '/users',
      '/appointments',
      '/reports',
      '/settings',
      '/profile',
      '/package',
      '/discount',
      '/orders',
      '/customer',
      '/fleet',
      '/employee',
      '/sales',
      '/campaign',
      '/analytics',
      '/campaign-editor',
      '/car-wash-website',
      '/website-builder',
      '/notifications',
      '/camera',
      '/rewards',
      '/payments',
    ],
    permissions: {
      canViewDashboard: true,
      canManageUsers: true,
      canManageAppointments: true,
      canViewReports: true,
      canManageSettings: true,
      canViewAttendance: false,
      canManageAttendance: false,
      canViewAnalytics: true,
      canManagePackages: true,
      canManageCustomers: true,
      canViewCRM: true,
      canManageFleet: true,
      canManageEmployees: true,
      canViewOrders: true,
      canManageOrders: true,
      canCancelOrders: true,
      canAddOrders: true,
      canViewPayments: true,
    },
  },
  [UserRole.ATTENDANT]: ATTENDANT_ROLE_CONFIG,
  [UserRole.ATTENDANT_PLUS]: {
    ...ATTENDANT_ROLE_CONFIG,
    role: UserRole.ATTENDANT_PLUS,
    displayName: 'Attendant plus',
    allowedRoutes: [...ATTENDANT_ROLE_CONFIG.allowedRoutes, '/customer'],
    permissions: {
      ...ATTENDANT_ROLE_CONFIG.permissions,
      canManageCustomers: true,
    },
  },
  // Attendance + profile only (Permission = staff). Must not share attendant ops routes.
  [UserRole.STAFF]: {
    role: UserRole.STAFF,
    displayName: 'Staff',
    defaultRoute: '/attendance',
    allowedRoutes: ['/attendance', '/profile'],
    permissions: createDefaultPermissions({
      canViewAttendance: true,
    }),
  },
  [UserRole.SUPER_ADMIN]: {
    role: UserRole.SUPER_ADMIN,
    displayName: 'Super Administrator',
    defaultRoute: '/admin/dashboard',
    allowedRoutes: [
      '/admin/login',
      '/admin/dashboard',
      '/admin/impersonation',
      '/admin/impersonation/shops',
      '/admin/impersonation/staff',
      '/admin/impersonation/tunnels',
    ],
    permissions: createDefaultPermissions({
      canViewDashboard: true,
      canManageUsers: true,
    }),
  },
  [UserRole.TUNNEL]: {
    role: UserRole.TUNNEL,
    displayName: 'Tunnel',
    defaultRoute: '/tunnel',
    allowedRoutes: ['/tunnel'],
    permissions: createDefaultPermissions({
      canViewOrders: true,
    }),
  },
};

/**
 * Base paths (leading slash, no trailing slash) for which `/base/...` is allowed when
 * the role's `allowedRoutes` includes that base. Single source for prefix matching in
 * `RoleService.hasRoutePermission` — add new routable sections here when they support subpaths.
 */
export const ROUTES_ALLOWING_SUBPATHS: readonly string[] = [
  '/campaign',
  '/campaign-editor',
  '/appointments',
  '/orders',
  '/customer',
  '/employee',
  '/fleet',
  '/package',
  '/rewards',
  '/sales',
  '/analytics',
  '/payments',
  '/car-wash-website',
  '/website-builder',
  '/camera',
  '/admin/impersonation',
];
